<template>
    <article :class="$style.card">
        <SteamGameGallery
            :class="$style.gallery_override"
            :name="props.game.name"
            :screenshots="props.game.screenshots"
            :priority="props.priority"
        />
        <div :class="$style.about">
            <div :class="$style.banner_wrap">
                <img
                    :class="$style.banner"
                    :src="props.game.banner"
                    :alt="$tr('steamGames.bannerAlt', { name: props.game.name })"
                    :loading="props.priority ? 'eager' : 'lazy'"
                    decoding="async"
                />
            </div>
            <div :class="$style.content">
                <div :class="$style.info">
                    <Typography
                        is="h2"
                        type="header"
                        size="s"
                        :responsive="false"
                        :class="$style.name"
                    >
                        {{ props.game.name }}
                    </Typography>
                    <Typography
                        is="p"
                        type="paragraph"
                        size="s"
                        :responsive="false"
                        :class="$style.description"
                    >
                        {{ props.description }}
                    </Typography>
                    <ul :class="$style.tags">
                        <li
                            v-for="tag in props.game.tags"
                            :key="tag"
                            :class="$style.tag"
                        >
                            <Typography
                                type="label"
                                size="xs"
                                accent
                                :responsive="false"
                            >
                                {{ tag }}
                            </Typography>
                        </li>
                    </ul>
                    <div :class="$style.reviews">
                        <div
                            v-for="item in reviews"
                            :key="item.label"
                            :class="$style.review"
                        >
                            <Typography
                                type="caption"
                                size="all-caps"
                                :class="$style.review_label"
                            >
                                {{ item.label }}
                            </Typography>
                            <Typography
                                type="caption"
                                size="m"
                                accent
                                :class="$style.review_value"
                            >
                                <span :class="$style[`verdict_${VERDICT_TONE[item.review.verdict]}`]">
                                    {{ verdictLabels[item.review.verdict] }}
                                </span>
                                <span :class="$style.review_count"> ({{ item.review.count }})</span>
                            </Typography>
                        </div>
                    </div>
                </div>
                <div :class="$style.cta_wrap">
                    <Button
                        :class="$style.cta_override"
                        color="salad"
                        size="l"
                        width="full"
                        leftIcon="steam"
                        :label="$tr('steamGames.cta')"
                        :externalTo="props.game.storeUrl"
                        target="_blank"
                        :interfaceTarget="`steam-store-link-${props.index}`"
                    />
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'

import { SteamGame, SteamVerdict } from '../games'
import SteamGameGallery from './SteamGameGallery.vue'

const props = defineProps<{
    game: SteamGame
    description: string
    priority?: boolean
    index: number
}>()

const tr = useTr()

const VERDICT_TONE: Record<SteamVerdict, 'positive' | 'mixed' | 'negative'> = {
    overwhelminglyPositive: 'positive',
    veryPositive: 'positive',
    positive: 'positive',
    mostlyPositive: 'positive',
    mixed: 'mixed',
    mostlyNegative: 'negative',
    negative: 'negative',
    veryNegative: 'negative',
    overwhelminglyNegative: 'negative',
}

const verdictLabels = computed<Record<SteamVerdict, string>>(() => ({
    overwhelminglyPositive: tr('steamGames.verdict.overwhelminglyPositive'),
    veryPositive: tr('steamGames.verdict.veryPositive'),
    positive: tr('steamGames.verdict.positive'),
    mostlyPositive: tr('steamGames.verdict.mostlyPositive'),
    mixed: tr('steamGames.verdict.mixed'),
    mostlyNegative: tr('steamGames.verdict.mostlyNegative'),
    negative: tr('steamGames.verdict.negative'),
    veryNegative: tr('steamGames.verdict.veryNegative'),
    overwhelminglyNegative: tr('steamGames.verdict.overwhelminglyNegative'),
}))

const reviews = computed(() => [
    { label: tr('steamGames.reviewsEnglish'), review: props.game.englishReviews },
    { label: tr('steamGames.reviewsRecent'), review: props.game.recentReviews },
])
</script>

<style module>
.card {
    --card-gutter: 12px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-bottom: 16px;
    overflow: hidden;
    border-radius: var(--radius-16);
    background: var(--experimental-purple-graphit);
}

.about,
.content {
    display: contents;
}

.gallery_override {
    order: 3;
}

.banner_wrap {
    position: relative;
    order: 1;
}

.banner_wrap::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(rgba(29, 28, 38, 0) 57.95%, var(--experimental-purple-graphit) 100%);
}

.banner {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 350 / 164;
    object-fit: cover;
}

.info {
    order: 2;
    margin-top: -8px;
    padding-inline: var(--card-gutter);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.name {
    line-height: 1.2;
}

.description {
    color: var(--white-80);
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding-block: 4px;
}

.tag {
    display: flex;
    align-items: center;
    height: 24px;
    padding: 0 12px;
    border-radius: 1000px;
    background: var(--experimental-carbon-purple-400);
    color: var(--white-80);
}

.reviews {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;
}

.review {
    display: flex;
    flex: 1 1 calc(50% - 10px);
    min-width: 0;
    flex-direction: column;
    gap: 4px;
    overflow-wrap: anywhere;
}

.review_label {
    color: var(--white-60);
    letter-spacing: 0.3px;
}

.review_value {
    line-height: 100%;
}

.review_count {
    color: var(--white-40);
}

.verdict_positive {
    color: var(--experimental-salad);
}

.verdict_mixed {
    color: var(--golden-500);
}

.verdict_negative {
    color: var(--rose-400);
}

.cta_wrap {
    order: 4;
    padding-inline: var(--card-gutter);
}

@media (--desktop-small-plus) {
    .card {
        display: grid;
        grid-template-columns: minmax(0, 1fr) clamp(360px, 45vw - 177px, 540px);
        gap: 10px;
        padding-bottom: 0;
        overflow: visible;
        border-radius: 0;
        background: none;
    }

    .about {
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow: hidden;
        border-radius: var(--radius-16);
        background: var(--experimental-purple-graphit);
    }

    .content {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        justify-content: space-between;
        gap: 24px;
        padding: 0 16px 16px;
    }

    .gallery_override,
    .banner_wrap,
    .info,
    .cta_wrap {
        order: 0;
        padding-inline: 0;
    }

    .info {
        margin-top: 0;
    }

    .cta_override {
        width: auto;
        height: 40px;
    }
}

@media (--desktop-large-plus) {
    .cta_override {
        height: 48px;
    }
}
</style>
